/**
 * @fileoverview Mock backend for the password-policy endpoint.
 * @description Registers GET /identity/password-policy on the feature's shared mock adapter (see
 * auth-mock-adapter.ts), so the password checklists work without a backend running. Its values
 * mirror the backend's default policy (IDENTITY_PASSWORD_* in its
 * identity.config.ts), so a password the mocked checklist accepts is one the real server accepts.
 * A no-op when VITE_ENABLE_MOCK_API is false, so switching to a real backend is deleting this
 * file's one import in features/auth/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import { buildMockSuccessEnvelope } from './mock-envelope.utils'

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

/** The mocked policy, also used by the reset-password mock to refuse a weak password (1034). */
export const MOCK_PASSWORD_POLICY: PasswordPolicy = {
  policyVersion: 'mock-1',
  minLength: 12,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
  disallowEmail: true,
  disallowName: true,
  previousPasswordsChecked: 3,
  ruleCodes: [
    'MIN_LENGTH',
    'MAX_LENGTH',
    'UPPERCASE',
    'LOWERCASE',
    'NUMBER',
    'SPECIAL',
    'NO_EMAIL',
    'NO_NAME',
    'PASSWORD_HISTORY',
  ],
}

if (authMockAdapter) {
  authMockAdapter
    .onGet('/identity/password-policy')
    .reply(() => [200, buildMockSuccessEnvelope(MOCK_PASSWORD_POLICY, 'Password policy retrieved')])
}
